// @givmo/app-admin — Company Fundraiser MANAGE page (EP-WEB3; the lifecycle FCM-WEB18).
//
// The manage surface for one company-owned fundraiser (`fundraisersGetOwnFundraiser` — the owner's
// private detail, which the acting company admin resolves to OWNER for). Supports: edit fields
// (name/description/goal → `fundraisersUpdateFundraiser`), replace cover, add/remove recipient items
// pre-freeze (RecipientPicker; soft-locked once a donation lands — the backend is authoritative and its
// error is surfaced regardless), publish a still-DRAFT fundraiser, and view aggregate stats. There is
// NO donor list (D-EP-13). Permission is Member-level — no Owner gate.
// EP-WEB15: whenever the private read carries it, a "What charities see" card holds the organization
// contact offer (D-FCM-15) over its own small form and Save.
//
// FCM-WEB18 (plan §FC-15, the mockup Jonathan ruled 2026-09-27): every lifecycle action shows exactly
// when the private read's `lifecycle` hints say the server would accept it — Close in the header beside
// Seed; Reopen and Reactivate in the state line under it; Deactivate, Delete draft, Delete forever and
// the funded "Need it taken down?" card at the foot of the page. A closed page, and a page Givmo took
// down, render as a read-only record (the server refuses their content edits: the closed freeze, and
// FCM-BE27 under the lock); so does a page a Close froze before it was deactivated (FCM-WEB28: the
// freeze is `closed_at`, whatever the status). The honest badge and the relabel ship at once; the
// buttons the §FC-15 flags gate light up when the server's hints do.
//
// DARK: reached only via `/company-fundraisers/$fundraiserId`, which redirects to /dashboard while
// `VITE_COMPANY_FUNDRAISERS_ENABLED` is off; this page also bounces (defense in depth).

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  companiesGetCompanyAdminOptions,
  companiesGetCompanyOptions,
  companiesListCompanyFundraisersQueryKey,
  fundraisersCloseFundraiserMutation,
  fundraisersDeleteFundraiserMutation,
  fundraisersGetOwnFundraiserOptions,
  fundraisersGetOwnFundraiserQueryKey,
  fundraisersPublishFundraiserMutation,
  fundraisersReactivateFundraiserMutation,
  fundraisersReopenFundraiserMutation,
  fundraisersUpdateFundraiserMutation,
} from '@givmo/api-client/query';
import { isGivmoError, type FundraiserOut, type FundraiserUpdate } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Icon,
  PageHeader,
  Skeleton,
  StatCard,
  TextField,
  Textarea,
  cn,
  saasButtonVariants,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { EyeOff, Lock, ShieldAlert } from 'lucide-react';
import { registerAssistantInsertionTarget } from '@givmo/assistant-panel';
import {
  ORG_CONTACT_OFFER_COPY,
  OrgContactOffer,
  contactOfferFromPrivateRead,
  orgContactProblem,
  orgContactRefusal,
  orgContactWrite,
  type OrgContactOfferState,
} from '@givmo/fundraisers-module';
import { isCompanyFundraisersEnabled } from '../../lib/env';
import { ConfirmDialog } from '../../components/confirm-dialog';
import {
  DESCRIPTION_MAX_LENGTH,
  LIFECYCLE_COPY,
  LIFECYCLE_TOASTS,
  NAME_MAX_LENGTH,
  closeDialogCopy,
  closedBannerCopy,
  fundraiserDonationCountForDisplay,
  fundraiserGoal,
  fundraiserGoalPercent,
  fundraiserRaised,
  fundraiserStatusView,
  goalUpdate,
  isClosed,
  isConflictRefusal,
  isDraft,
  isPublished,
  isReadOnlyRecord,
  itemsFrozen,
  lifecycleActions,
  parseGoalDollars,
  staleLifecycleRefusal,
  takedownQuestionHref,
  takedownSupportHref,
} from './company-fundraisers-decode';
import { CoverImageInput } from './cover-image-input';
import { RecipientPicker } from './recipient-picker';
import { SeedFundraiserButton } from './seed-fundraiser-button';

const PREFIX = 'company-fundraiser-detail';

/** Below `lg` a 36px button keeps a 44px tall hit area without changing how it looks (the contact
 *  card's Save uses the same pseudo-element). */
const PHONE_HIT = 'max-lg:after:absolute max-lg:after:inset-x-0 max-lg:after:-inset-y-4';
/** A link inside a sentence: kept on one line, with a 24px tall hit area (44px below `lg`) that leaves
 *  the sentence's own line height alone. */
const INLINE_HIT =
  'relative whitespace-nowrap after:absolute after:inset-x-0 after:-inset-y-4 max-lg:after:-inset-y-14';

type Dialog = 'close' | 'reopen' | 'deactivate' | 'deleteDraft' | 'deleteForever' | null;
type RemoveKind = 'deactivate' | 'deleteDraft' | 'deleteForever';

export interface CompanyFundraiserDetailPageProps {
  fundraiserId: string;
}

export function CompanyFundraiserDetailPage({ fundraiserId }: CompanyFundraiserDetailPageProps) {
  const navigate = useNavigate();
  const enabled = isCompanyFundraisersEnabled();

  useEffect(() => {
    if (!enabled) void navigate({ to: '/dashboard' });
  }, [enabled, navigate]);
  if (!enabled) return null;

  return <DetailView fundraiserId={fundraiserId} />;
}

function DetailView({ fundraiserId }: { fundraiserId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<Dialog>(null);

  const detail = useQuery(
    fundraisersGetOwnFundraiserOptions({ path: { fundraiser_id: fundraiserId } }),
  );
  // EP-WEB15 — the company read supplies the name and the email domain the contact offer states.
  const company = useQuery(companiesGetCompanyOptions());

  const refetch = async () => {
    await queryClient.invalidateQueries({
      queryKey: fundraisersGetOwnFundraiserQueryKey({ path: { fundraiser_id: fundraiserId } }),
    });
  };
  const invalidateList = () =>
    queryClient.invalidateQueries({ queryKey: companiesListCompanyFundraisersQueryKey() });
  // A refused lifecycle write means the page is stale. Say why (plainly for the typed codes: Givmo
  // took the fundraiser down, or a donation now locks it; otherwise the server's words), and on any
  // 409 re-read the page, so it shows what the server holds.
  const failed = (error: unknown, fallback: string) => {
    setDialog(null);
    const stale = staleLifecycleRefusal(error);
    toast.error(stale ?? (isGivmoError(error) ? error.message : fallback));
    if (stale !== null || isConflictRefusal(error)) {
      void refetch();
      void invalidateList();
    }
  };

  const publish = useMutation({
    ...fundraisersPublishFundraiserMutation(),
    onSuccess: async () => {
      toast.success('Fundraiser published.');
      await refetch();
      void invalidateList();
    },
    onError: (error) =>
      toast.error(
        isGivmoError(error)
          ? error.message
          : 'We couldn’t publish the fundraiser. Please try again.',
      ),
  });

  // The DELETE, named for what it does: an open row DEACTIVATES; a Draft, and an unfunded Deactivated
  // row, are deleted for good, and the page returns to the list.
  const deleteOp = fundraisersDeleteFundraiserMutation();
  const remove = useMutation({
    mutationFn: async (kind: RemoveKind) => {
      await deleteOp.mutationFn?.({ path: { fundraiser_id: fundraiserId } }, undefined as never);
      return kind;
    },
    onSuccess: async (kind) => {
      setDialog(null);
      if (kind === 'deactivate') {
        toast.success(LIFECYCLE_TOASTS.deactivated);
        await refetch();
        void invalidateList();
        return;
      }
      toast.success(
        kind === 'deleteDraft' ? LIFECYCLE_TOASTS.draftDeleted : LIFECYCLE_TOASTS.deleted,
      );
      void invalidateList();
      void navigate({ to: '/company-fundraisers' });
    },
    onError: (error) => failed(error, 'We couldn’t make that change. Please try again.'),
  });

  // Close and Reopen: the consumer routes (FCM-BE14), dark behind the backend's close flag, when the
  // hints never offer them.
  const close = useMutation({
    ...fundraisersCloseFundraiserMutation(),
    onSuccess: async () => {
      setDialog(null);
      toast.success(LIFECYCLE_TOASTS.closed);
      await refetch();
      void invalidateList();
    },
    onError: (error) => failed(error, 'We couldn’t close the fundraiser. Please try again.'),
  });
  const reopen = useMutation({
    ...fundraisersReopenFundraiserMutation(),
    onSuccess: async () => {
      setDialog(null);
      toast.success(LIFECYCLE_TOASTS.reopened);
      await refetch();
      void invalidateList();
    },
    onError: (error) => failed(error, 'We couldn’t reopen the fundraiser. Please try again.'),
  });
  // Reactivate runs at once, as in the iOS app: it only restores.
  const reactivate = useMutation({
    ...fundraisersReactivateFundraiserMutation(),
    onSuccess: async () => {
      toast.success(LIFECYCLE_TOASTS.reactivated);
      await refetch();
      void invalidateList();
    },
    onError: (error) => failed(error, 'We couldn’t reactivate the fundraiser. Please try again.'),
  });

  if (detail.isPending) {
    return (
      <div
        className="mx-auto flex w-full max-w-[820px] flex-col gap-24 px-24 py-24"
        {...testIdProp(`${PREFIX}-loading`)}
      >
        <Skeleton className="h-10 w-[280px] rounded" />
        <Skeleton className="h-[140px] w-full rounded-lg" />
        <Skeleton className="h-[220px] w-full rounded-lg" />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div
        className="mx-auto flex w-full max-w-[820px] flex-col gap-16 px-24 py-24"
        {...testIdProp(`${PREFIX}-error`)}
      >
        <p className="text-s14 font-w500 text-danger-700">
          {isGivmoError(detail.error) ? detail.error.message : 'Failed to load this fundraiser.'}
        </p>
        <div className="flex gap-8">
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={() => void detail.refetch()}
            testId={`${PREFIX}-retry`}
          >
            Try again
          </Button>
          <Button
            appearance="saas"
            tone="ghost"
            size="sm"
            onClick={() => void navigate({ to: '/company-fundraisers' })}
          >
            Back to list
          </Button>
        </div>
      </div>
    );
  }

  const fundraiser: FundraiserOut = detail.data;
  const offer = contactOfferFromPrivateRead(fundraiser);
  // The name the offer states once the company read settles (the byline's if that read fails).
  const offerOrgName =
    offer !== null && !company.isPending
      ? (company.data?.name ?? fundraiser.owner_company?.name ?? null)
      : null;
  const status = fundraiserStatusView(fundraiser);
  const actions = lifecycleActions(fundraiser);
  const closed = isClosed(fundraiser);
  const takenDown = actions.removedByGivmo;
  const readOnly = isReadOnlyRecord(fundraiser);
  // FCM-WEB28: a page a Close froze before it was deactivated is a record too, but it is neither
  // Closed (Reopen comes only after Reactivate) nor taken down, so neither of their lines fits it.
  const frozenDeactivated = readOnly && !closed && !takenDown;
  const frozen = itemsFrozen(fundraiser);
  const goalPercent = fundraiserGoalPercent(fundraiser);
  const busy = remove.isPending || close.isPending || reopen.isPending || reactivate.isPending;
  const path = { fundraiser_id: fundraiser.id };
  const recipientReason =
    frozen || !readOnly
      ? undefined
      : closed
        ? LIFECYCLE_COPY.closedRecipients
        : takenDown
          ? LIFECYCLE_COPY.takenDownDetails
          : LIFECYCLE_COPY.frozenDeactivatedRecipients;

  return (
    <div
      className="mx-auto flex w-full max-w-[820px] flex-col gap-24 px-24 py-24"
      {...testIdProp('page-company-fundraiser-detail')}
    >
      <PageHeader
        title={
          <span className="flex items-center gap-10">
            <span className="truncate">{fundraiser.name}</span>
            <Badge tone={status.tone} dot testId={`${PREFIX}-status`}>
              {status.label}
            </Badge>
          </span>
        }
        actions={
          <div className="flex items-center gap-8">
            {/* A closed fundraiser takes no donation, the company's own seed included. */}
            <SeedFundraiserButton
              fundraiserId={fundraiser.id}
              published={isPublished(fundraiser) && !closed}
            />
            {actions.close && (
              <Button
                appearance="saas"
                tone="secondary"
                className={PHONE_HIT}
                disabled={busy}
                onClick={() => setDialog('close')}
                testId={`${PREFIX}-close`}
              >
                Close fundraiser
              </Button>
            )}
          </div>
        }
      />

      {/* The state line: what a non-live state means, and the way back where there is one. */}
      {closed && (
        <StateNotice
          icon={Lock}
          testId={`${PREFIX}-closed-notice`}
          action={
            actions.reopen ? (
              <Button
                appearance="saas"
                tone="secondary"
                className={PHONE_HIT}
                disabled={busy}
                onClick={() => setDialog('reopen')}
                testId={`${PREFIX}-reopen`}
              >
                Reopen
              </Button>
            ) : null
          }
        >
          {closedBannerCopy(fundraiser)}
        </StateNotice>
      )}
      {fundraiser.status === 'Deactivated' && !takenDown && (
        <StateNotice
          icon={EyeOff}
          testId={`${PREFIX}-deactivated-notice`}
          action={
            actions.reactivate ? (
              <Button
                appearance="saas"
                tone="secondary"
                className={PHONE_HIT}
                loading={reactivate.isPending}
                disabled={busy}
                onClick={() => reactivate.mutate({ path })}
                testId={`${PREFIX}-reactivate`}
              >
                Reactivate
              </Button>
            ) : null
          }
        >
          {LIFECYCLE_COPY.deactivatedBanner}
        </StateNotice>
      )}
      {takenDown && (
        <StateNotice icon={ShieldAlert} testId={`${PREFIX}-takedown-notice`} action={null}>
          {LIFECYCLE_COPY.takedownLead}{' '}
          <a
            href={takedownQuestionHref(fundraiser)}
            className={cn(
              'font-w600 text-brand-700 underline-offset-2 hover:underline',
              INLINE_HIT,
            )}
            {...testIdProp(`${PREFIX}-takedown-support`)}
          >
            {LIFECYCLE_COPY.takedownLink}
          </a>{' '}
          {LIFECYCLE_COPY.takedownTail}
        </StateNotice>
      )}

      {/* ── Stats ── */}
      <div className="grid grid-cols-1 gap-12 sm:grid-cols-3" {...testIdProp(`${PREFIX}-stats`)}>
        <StatCard
          label="Raised"
          value={fundraiserRaised(fundraiser) ?? 'Unavailable'}
          testId={`${PREFIX}-stat-raised`}
        />
        <StatCard
          label="Donations"
          value={String(fundraiserDonationCountForDisplay(fundraiser) ?? 'Unavailable')}
          testId={`${PREFIX}-stat-donations`}
        />
        <StatCard
          label={goalPercent === null ? 'Goal' : 'Goal progress'}
          value={
            goalPercent !== null ? `${goalPercent}%` : (fundraiserGoal(fundraiser) ?? <NoGoal />)
          }
          testId={`${PREFIX}-stat-goal`}
        />
      </div>

      {/* ── Cover ── */}
      <Card>
        <CardHeader title="Cover image" />
        <CardBody>
          <CoverImageInput
            fundraiserId={fundraiser.id}
            imageUrl={fundraiser.image_url}
            onUploaded={refetch}
            readOnly={readOnly}
            onRefused={(error) => {
              const stale = staleLifecycleRefusal(error);
              if (stale !== null) {
                toast.error(stale);
                void refetch();
                return true;
              }
              // Any other 409: the input shows the server's words, and the page re-reads.
              if (isConflictRefusal(error)) void refetch();
              return false;
            }}
          />
        </CardBody>
      </Card>

      {/* ── Details (a fundraiser a Close froze, or Givmo took down, shows them as a record) ── */}
      <Card>
        <CardHeader
          title="Details"
          subtitle={
            closed
              ? LIFECYCLE_COPY.closedDetails
              : takenDown
                ? LIFECYCLE_COPY.takenDownDetails
                : frozenDeactivated
                  ? LIFECYCLE_COPY.frozenDeactivatedDetails
                  : 'Name, description, and goal stay editable after publishing.'
          }
        />
        <CardBody>
          {readOnly ? (
            <DetailsRecord fundraiser={fundraiser} />
          ) : (
            <DetailsEditor
              key={fundraiser.id}
              fundraiser={fundraiser}
              onSaved={refetch}
              onStale={async () => {
                await refetch();
                void invalidateList();
              }}
            />
          )}
        </CardBody>
      </Card>

      {/* ── Recipients ── */}
      <Card>
        <CardHeader
          title="Recipients"
          subtitle="Where donations go, split equally across each item."
        />
        <CardBody>
          <RecipientPicker
            fundraiser={fundraiser}
            onChanged={refetch}
            disabled={frozen || readOnly}
            disabledReason={recipientReason}
          />
        </CardBody>
      </Card>

      {/* ── What charities see (EP-WEB15; only while the private read carries the offer). A
          disclosure preference the server keeps editable on a closed or taken-down row. ── */}
      {offer !== null && offerOrgName !== null && (
        <Card testId={`${PREFIX}-contact-card`}>
          <CardHeader
            title={ORG_CONTACT_OFFER_COPY.heading}
            subtitle={ORG_CONTACT_OFFER_COPY.notice(offerOrgName)}
          />
          <CardBody>
            <ContactOfferEditor
              key={fundraiser.id}
              fundraiserId={fundraiser.id}
              offer={offer}
              orgName={offerOrgName}
              domain={company.data?.domain?.trim() || null}
              onSaved={refetch}
            />
          </CardBody>
        </Card>
      )}

      {/* ── Publish (draft only) ── */}
      {isDraft(fundraiser) && (
        <Card>
          <CardHeader
            title="Publish"
            subtitle="Publishing makes the fundraiser live and donatable."
          />
          <CardBody className="flex items-center justify-end">
            <Button
              appearance="saas"
              tone="primary"
              size="lg"
              loading={publish.isPending}
              onClick={() => publish.mutate({ path })}
              testId={`${PREFIX}-publish`}
            >
              Publish fundraiser
            </Button>
          </CardBody>
        </Card>
      )}

      {/* ── The foot: taking it down, in the org console's deactivation-card idiom ── */}
      {actions.deleteDraft ? (
        <FootCard
          title={LIFECYCLE_COPY.deleteDraftCardTitle}
          body={LIFECYCLE_COPY.deleteDraftCardBody}
          testId={`${PREFIX}-delete-draft-card`}
          action={
            <FootButton
              label={LIFECYCLE_COPY.deleteDraftConfirm}
              disabled={busy}
              onClick={() => setDialog('deleteDraft')}
              testId={`${PREFIX}-delete-draft`}
            />
          }
        />
      ) : actions.deactivate ? (
        <FootCard
          title={LIFECYCLE_COPY.deactivateCardTitle}
          body={LIFECYCLE_COPY.deactivateCardBody}
          testId={`${PREFIX}-deactivate-card`}
          action={
            <FootButton
              label={LIFECYCLE_COPY.deactivateConfirm}
              disabled={busy}
              onClick={() => setDialog('deactivate')}
              testId={`${PREFIX}-deactivate`}
            />
          }
        />
      ) : actions.contactSupport ? (
        <FootCard
          title={LIFECYCLE_COPY.fundedTitle}
          body={LIFECYCLE_COPY.fundedBody}
          testId={`${PREFIX}-takedown-help`}
          action={
            <a
              href={takedownSupportHref(fundraiser)}
              className={cn(
                saasButtonVariants({ tone: 'secondary', size: 'md' }),
                'relative shrink-0 self-start sm:self-auto',
                PHONE_HIT,
              )}
              {...testIdProp(`${PREFIX}-contact-support`)}
            >
              {LIFECYCLE_COPY.fundedAction}
            </a>
          }
        />
      ) : actions.deleteForever ? (
        <FootCard
          title={LIFECYCLE_COPY.deleteForeverCardTitle}
          body={LIFECYCLE_COPY.deleteForeverCardBody}
          testId={`${PREFIX}-delete-forever-card`}
          action={
            <FootButton
              label={LIFECYCLE_COPY.deleteForeverCardTitle}
              disabled={busy}
              onClick={() => setDialog('deleteForever')}
              testId={`${PREFIX}-delete-forever`}
            />
          }
        />
      ) : null}

      <ConfirmDialog
        open={dialog === 'close'}
        onOpenChange={(open) => {
          if (!open && !close.isPending) setDialog(null);
        }}
        title={LIFECYCLE_COPY.closeTitle}
        subject={fundraiser.name}
        body={closeDialogCopy(fundraiser)}
        confirmLabel={LIFECYCLE_COPY.closeConfirm}
        loading={close.isPending}
        onConfirm={() => close.mutate({ path })}
        testId={`${PREFIX}-close-confirm`}
      />
      <ConfirmDialog
        open={dialog === 'reopen'}
        onOpenChange={(open) => {
          if (!open && !reopen.isPending) setDialog(null);
        }}
        title={LIFECYCLE_COPY.reopenTitle}
        subject={fundraiser.name}
        body={LIFECYCLE_COPY.reopenBody}
        confirmLabel={LIFECYCLE_COPY.reopenConfirm}
        loading={reopen.isPending}
        onConfirm={() => reopen.mutate({ path })}
        testId={`${PREFIX}-reopen-confirm`}
      />
      <ConfirmDialog
        open={dialog === 'deactivate'}
        onOpenChange={(open) => {
          if (!open && !remove.isPending) setDialog(null);
        }}
        title={LIFECYCLE_COPY.deactivateTitle}
        subject={fundraiser.name}
        body={LIFECYCLE_COPY.deactivateBody}
        confirmLabel={LIFECYCLE_COPY.deactivateConfirm}
        destructive
        loading={remove.isPending}
        onConfirm={() => remove.mutate('deactivate')}
        testId={`${PREFIX}-deactivate-confirm`}
      />
      <ConfirmDialog
        open={dialog === 'deleteDraft'}
        onOpenChange={(open) => {
          if (!open && !remove.isPending) setDialog(null);
        }}
        title={LIFECYCLE_COPY.deleteDraftTitle}
        subject={fundraiser.name}
        body={LIFECYCLE_COPY.cantBeUndone}
        confirmLabel={LIFECYCLE_COPY.deleteDraftConfirm}
        destructive
        loading={remove.isPending}
        onConfirm={() => remove.mutate('deleteDraft')}
        testId={`${PREFIX}-delete-draft-confirm`}
      />
      <ConfirmDialog
        open={dialog === 'deleteForever'}
        onOpenChange={(open) => {
          if (!open && !remove.isPending) setDialog(null);
        }}
        title={LIFECYCLE_COPY.deleteForeverTitle}
        subject={fundraiser.name}
        body={LIFECYCLE_COPY.cantBeUndone}
        confirmLabel={LIFECYCLE_COPY.deleteForeverConfirm}
        destructive
        loading={remove.isPending}
        onConfirm={() => remove.mutate('deleteForever')}
        testId={`${PREFIX}-delete-forever-confirm`}
      />
    </div>
  );
}

/** The Goal tile with no goal: a muted em dash that screen readers hear as "No goal" (ruling Q1). */
function NoGoal() {
  return (
    <>
      <span className="text-text-tertiary" aria-hidden="true">
        —
      </span>
      <span className="sr-only">No goal</span>
    </>
  );
}

/** The line under the header for a non-live state, in the house notice recipe
 *  (`rounded-lg border border-border-subtle bg-surface-sunken p-12`, as the gift-card notices). */
function StateNotice({
  icon,
  action,
  testId,
  children,
}: {
  icon: typeof Lock;
  action: ReactNode;
  testId: string;
  children: ReactNode;
}) {
  return (
    <div
      role="status"
      className="flex flex-col gap-12 rounded-lg border border-border-subtle bg-surface-sunken p-12 sm:flex-row sm:items-center sm:justify-between sm:gap-16 sm:py-10 sm:pl-12 sm:pr-10"
      {...testIdProp(testId)}
    >
      <div className="flex items-start gap-8">
        <Icon icon={icon} size={16} aria-hidden className="mt-1 shrink-0 text-text-secondary" />
        <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-text-strong">
          {children}
        </p>
      </div>
      {action !== null && <div className="shrink-0 self-start sm:self-auto">{action}</div>}
    </div>
  );
}

/** A foot card, mirroring the org console's `OrgDeactivationCard` (packages/org-console). */
function FootCard({
  title,
  body,
  action,
  testId,
}: {
  title: string;
  body: string;
  action: ReactNode;
  testId: string;
}) {
  return (
    <Card testId={testId}>
      <CardBody className="flex flex-col gap-12 sm:flex-row sm:items-center sm:justify-between sm:gap-16">
        <div className="flex min-w-0 flex-col gap-4">
          <h3 className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">{title}</h3>
          <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {body}
          </p>
        </div>
        {action}
      </CardBody>
    </Card>
  );
}

function FootButton({
  label,
  disabled,
  onClick,
  testId,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <Button
      appearance="saas"
      tone="secondary"
      size="md"
      className={cn('shrink-0 self-start sm:self-auto', PHONE_HIT)}
      disabled={disabled}
      onClick={onClick}
      testId={testId}
    >
      {label}
    </Button>
  );
}

/** A closed or taken-down fundraiser's details, read-only, as a record rather than a greyed-out form
 *  (ruling Q5): the form's label type over the value at the form's 16px. */
function DetailsRecord({ fundraiser }: { fundraiser: FundraiserOut }) {
  const description = fundraiser.description?.trim() ?? '';
  const goal = fundraiserGoal(fundraiser);
  const rows: [string, string, ReactNode][] = [
    ['name', 'Name', fundraiser.name ?? ''],
    ['description', 'Description', description.length > 0 ? description : <EmptyValue />],
    ['goal', 'Goal', goal ?? <NoGoal />],
  ];
  return (
    <dl className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-details-record`)}>
      {rows.map(([key, term, value]) => (
        <div key={key} className="flex flex-col gap-4">
          <dt className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
            {term}
          </dt>
          <dd
            className="whitespace-pre-line break-words font-sans text-s16 leading-h15 tracking-givmo text-text-strong"
            {...testIdProp(`${PREFIX}-record-${key}`)}
          >
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function EmptyValue() {
  return (
    <>
      <span className="text-text-tertiary" aria-hidden="true">
        —
      </span>
      <span className="sr-only">None</span>
    </>
  );
}

/** The edit-fields form. Its own local state (seeded ONCE from props via the parent's `key={id}`, so a
 *  cover/item refetch never clobbers in-progress typing). Saves via `fundraisersUpdateFundraiser`. */
function DetailsEditor({
  fundraiser,
  onSaved,
  onStale,
}: {
  fundraiser: FundraiserOut;
  onSaved: () => void | Promise<unknown>;
  /** A save the server refused because the page went stale (any 409: a takedown or a close landed
   *  while it was open). */
  onStale: () => void | Promise<unknown>;
}) {
  const [name, setName] = useState(fundraiser.name ?? '');
  const [description, setDescription] = useState(fundraiser.description ?? '');

  // EP-AI1 — offer this field to the assistant panel while the page is on screen.
  //
  // ⚠ THIS PAGE ADDS NO ASSISTANT BUTTON (D-EP-45). The affordance is the panel's own prompts; all
  // this registration does is tell an already-open panel that there is somewhere to put a draft, so
  // its reply offers "Insert into description" instead of Copy alone. With the assistant's switch
  // off nothing is ever listening and the registration is inert.
  //
  // It writes into the LOCAL draft, never through the save mutation: the person edits what arrives
  // and presses Save themselves (D-EP-38/D8 — the assistant proposes, the administrator decides).
  useEffect(
    () =>
      registerAssistantInsertionTarget({
        actionLabel: 'Insert into description',
        insert: (text) => setDescription(text),
      }),
    [],
  );
  const [goalRaw, setGoalRaw] = useState(
    typeof fundraiser.goal_amount === 'number' ? String(fundraiser.goal_amount) : '',
  );
  const [goalError, setGoalError] = useState<string | null>(null);

  const update = useMutation({
    ...fundraisersUpdateFundraiserMutation(),
    onSuccess: async () => {
      await onSaved();
      toast.success('Fundraiser updated.');
    },
    onError: (error) => {
      const stale = staleLifecycleRefusal(error);
      toast.error(
        stale ??
          (isGivmoError(error)
            ? error.message
            : 'We couldn’t save your changes. Please try again.'),
      );
      if (stale !== null || isConflictRefusal(error)) void onStale();
    },
  });

  function save() {
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      setGoalError(null);
      toast.error('Give your fundraiser a name.');
      return;
    }
    const goal = parseGoalDollars(goalRaw);
    if ('error' in goal) {
      setGoalError(goal.error);
      return;
    }
    setGoalError(null);
    // The PUT is skip-None: a null `goal_amount` KEEPS the old goal while the page toasts success. An
    // emptied field therefore removes a saved goal with `remove_goal` (FCM-BE13), via `goalUpdate`.
    const body: FundraiserUpdate = {
      name: trimmedName,
      description: description.trim(),
      ...goalUpdate(goal.value, fundraiser.goal_amount),
    };
    update.mutate({ path: { fundraiser_id: fundraiser.id }, body });
  }

  return (
    <div className="flex flex-col gap-16">
      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={NAME_MAX_LENGTH}
        testId={`${PREFIX}-name`}
      />
      <Textarea
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        maxLength={DESCRIPTION_MAX_LENGTH}
        rows={4}
        testId={`${PREFIX}-description`}
      />
      <TextField
        label="Goal (USD, optional)"
        inputMode="decimal"
        value={goalRaw}
        onChange={(e) => setGoalRaw(e.target.value)}
        error={goalError !== null}
        errorMessage={goalError ?? undefined}
        className="max-w-[220px]"
        testId={`${PREFIX}-goal`}
      />
      <div className="flex items-center justify-end">
        <Button
          appearance="saas"
          tone="primary"
          loading={update.isPending}
          disabled={name.trim().length === 0 || update.isPending}
          onClick={save}
          testId={`${PREFIX}-save`}
        >
          Save changes
        </Button>
      </div>
    </div>
  );
}

/**
 * The contact card's form (EP-WEB15). It follows the page's own Details convention: Save is always
 * enabled and checks on submit — a problem shows on the field, focus moves there, and nothing is
 * written. Otherwise ONE PUT carries the choice, plus the address only when it differs from what the
 * read offered (switching on with that address unchanged writes the switch alone, and the server
 * resolves whose address it is). Until the person edits, both values follow the read, so a row with
 * no address yet shows the admin's own email and stores it only on save.
 */
function ContactOfferEditor({
  fundraiserId,
  offer,
  orgName,
  domain,
  onSaved,
}: {
  fundraiserId: string;
  offer: OrgContactOfferState;
  orgName: string;
  domain: string | null;
  onSaved: () => void | Promise<unknown>;
}) {
  const me = useQuery(companiesGetCompanyAdminOptions());
  const ownEmail = me.data?.email?.trim() || null;
  const [sharedEdit, setSharedEdit] = useState<boolean | null>(null);
  const [emailEdit, setEmailEdit] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  const shared = sharedEdit ?? offer.shared;
  const email = emailEdit ?? offer.email ?? ownEmail ?? '';
  const rule = { ownEmail, offeredEmail: offer.email, domain };

  const update = useMutation({
    ...fundraisersUpdateFundraiserMutation(),
    onSuccess: async () => {
      await onSaved();
      // The card now follows the read again, which states what was stored.
      setSharedEdit(null);
      setEmailEdit(null);
      setShowErrors(false);
      toast.success('Fundraiser updated.');
    },
    onError: (error) => {
      const refusal = orgContactRefusal(error);
      if (refusal !== null) setServerError(refusal);
      else
        toast.error(
          isGivmoError(error) ? error.message : 'We couldn’t save your changes. Please try again.',
        );
    },
  });

  function save() {
    // A new attempt withdraws the last refusal; this one speaks for itself.
    setServerError(null);
    if (orgContactProblem(shared, email, rule) !== null) {
      setShowErrors(true);
      field.current?.focus();
      return;
    }
    update.mutate({
      path: { fundraiser_id: fundraiserId },
      body: orgContactWrite(shared, email, offer) ?? { share_org_contact: shared },
    });
  }

  return (
    <div className="flex flex-col gap-16">
      <OrgContactOffer
        orgName={orgName}
        rule={rule}
        shared={shared}
        email={email}
        onSharedChange={(value) => {
          setSharedEdit(value);
          setServerError(null);
        }}
        onEmailChange={(value) => {
          setEmailEdit(value);
          setServerError(null);
        }}
        serverError={serverError}
        showErrors={showErrors}
        inputRef={field}
        disabled={update.isPending}
        lead="none"
        testIdPrefix={PREFIX}
      />
      <div className="flex items-center justify-end">
        <Button
          appearance="saas"
          tone="primary"
          loading={update.isPending}
          disabled={update.isPending}
          onClick={save}
          className={PHONE_HIT}
          testId={`${PREFIX}-contact-save`}
        >
          Save changes
        </Button>
      </div>
    </div>
  );
}
